/**
 * Filed items mirror their line (#337, plan ruling R1). A filed item's first
 * paragraph holds a copy of the inline content of the line its `ref` names;
 * this plugin keeps every copy identical inside the same transaction, so the
 * two can never disagree: an edit to the line goes to its items, an edit to an
 * item goes to the line and the line's other items. Only inline content is
 * copied - never flags, lineId or block type.
 *
 * - An edit that crosses a block boundary (a selection spanning two items, a
 *   join) never writes back to a line: the line wins and the item is put back.
 * - A line joined into the line above keeps its items: they follow the joined
 *   line. A line deleted outright takes its items with it (ruling R2), in the
 *   same transaction, so one undo restores both.
 * - On load nothing is ever deleted: an item whose line is not in this editor
 *   may simply be outside it.
 */
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Mapping } from '@tiptap/pm/transform';
import type { Fragment, Node as PmNode } from '@tiptap/pm/model';
import { LINE_SOURCES, newLineId } from './LineIdExtension';

interface Block { pos: number; node: PmNode }
interface Filed { item: Block; para: Block; ref: string }
interface Change { from: number; to: number; crossing: boolean }

function collect(doc: PmNode): { sources: Map<string, Block>; filed: Filed[] } {
  const sources = new Map<string, Block>();
  const filed: Filed[] = [];
  doc.descendants((n, pos, parent) => {
    if (n.type.name === 'namedListItem' && n.attrs.ref) {
      filed.push({ item: { pos, node: n }, para: { pos: pos + 1, node: n.firstChild! }, ref: n.attrs.ref });
    } else if (LINE_SOURCES.has(n.type.name) && n.attrs.lineId && parent?.type.name !== 'namedListItem' && !sources.has(n.attrs.lineId)) {
      sources.set(n.attrs.lineId, { pos, node: n });
    }
    return true;
  });
  return { sources, filed };
}

/** What these transactions changed, as ranges of the final document. */
function changes(trs: readonly Transaction[]): Change[] {
  const out: Change[] = [];
  trs.forEach((tr, t) => {
    tr.steps.forEach((step, i) => {
      const before = tr.docs[i];
      const toFinal = (from: number, to: number, crossing: boolean) => {
        for (let j = i + 1; j < tr.mapping.maps.length; j++) {
          from = tr.mapping.maps[j].map(from, -1);
          to = tr.mapping.maps[j].map(to, 1);
        }
        for (let k = t + 1; k < trs.length; k++) {
          from = trs[k].mapping.map(from, -1);
          to = trs[k].mapping.map(to, 1);
        }
        out.push({ from, to, crossing });
      };
      // A step whose old range starts and ends in different blocks joins or splits them.
      const crosses = (os: number, oe: number) => {
        try { return !before.resolve(os).sameParent(before.resolve(oe)); } catch { return true; }
      };
      let mapped = false;
      tr.mapping.maps[i].forEach((os, oe, ns, ne) => { mapped = true; toFinal(ns, ne, crosses(os, oe)); });
      // Mark and attribute steps change no positions, so their map is empty.
      const { from, to } = step as unknown as { from?: number; to?: number };
      if (!mapped && typeof from === 'number') {
        const end = typeof to === 'number' ? to : from + 1;
        toFinal(from, end, crosses(from, end));
      }
    });
  });
  return out;
}

const touchedBy = (b: Block, cs: Change[]) =>
  cs.filter((c) => c.from <= b.pos + b.node.nodeSize && c.to >= b.pos);

/**
 * The transaction that brings every copy in line, or null. Without `cs`
 * (opening a document) every item is made to match its source and nothing is
 * deleted. `old` and `mapping` let a line joined into its neighbour keep its items.
 */
export function syncMirrors(state: EditorState, cs: Change[] | null, old?: EditorState, mapping?: Mapping): Transaction | null {
  const { sources, filed } = collect(state.doc);
  if (!filed.length) return null;

  // Which content wins for each line: an edited source, else an item edited
  // within itself, else the source (which also undoes a crossing edit to an item).
  const winner = new Map<string, Fragment>();
  if (cs) {
    for (const [id, src] of sources) if (touchedBy(src, cs).length) winner.set(id, src.node.content);
    for (const f of filed) {
      if (winner.has(f.ref) || !sources.has(f.ref)) continue;
      const hits = touchedBy(f.para, cs);
      if (!hits.length) continue;
      winner.set(f.ref, hits.some((h) => h.crossing) ? sources.get(f.ref)!.node.content : f.para.node.content);
    }
  } else {
    for (const [id, src] of sources) winner.set(id, src.node.content);
  }

  type Op = { at: number; apply: (tr: Transaction) => void };
  const ops: Op[] = [];
  const deleted: Array<[number, number]> = [];
  const inDeleted = (pos: number) => deleted.some(([a, b]) => pos >= a && pos < b);

  // Items whose line is gone: follow the line it was joined into, or go with it.
  if (cs && old && mapping) {
    const oldLines = collect(old.doc).sources;
    const repointed = new Map<string, { pos: number; node: PmNode; id: string }>();
    for (const f of filed) {
      if (sources.has(f.ref)) continue;
      let target = repointed.get(f.ref);
      if (!target) {
        const was = oldLines.get(f.ref);
        const text = was?.node.textContent ?? '';
        const $m = was ? state.doc.resolve(Math.min(mapping.map(was.pos, -1), state.doc.content.size)) : null;
        const block = $m ? ($m.parent.isTextblock ? { pos: $m.before($m.depth), node: $m.parent } : ($m.nodeAfter?.isTextblock ? { pos: $m.pos, node: $m.nodeAfter } : null)) : null;
        const ok = block && text && LINE_SOURCES.has(block.node.type.name) && block.node.textContent.includes(text)
          && $m!.node(Math.max(0, $m!.depth - 1)).type.name !== 'namedListItem';
        if (ok) {
          target = { pos: block!.pos, node: block!.node, id: block!.node.attrs.lineId || newLineId() };
          repointed.set(f.ref, target);
          if (!block!.node.attrs.lineId) {
            const t = target;
            ops.push({ at: t.pos, apply: (tr) => tr.setNodeMarkup(t.pos, undefined, { ...t.node.attrs, lineId: t.id }) });
          }
        }
      }
      if (target) {
        const t = target;
        ops.push({ at: f.para.pos + 1, apply: (tr) => tr.replaceWith(f.para.pos + 1, f.para.pos + 1 + f.para.node.content.size, t.node.content) });
        ops.push({ at: f.item.pos, apply: (tr) => tr.setNodeMarkup(f.item.pos, undefined, { ...f.item.node.attrs, ref: t.id }) });
        continue;
      }
      const from = f.item.pos;
      const to = from + f.item.node.nodeSize;
      if (inDeleted(from)) continue;
      deleted.push([from, to]);
      ops.push({ at: from, apply: (tr) => tr.delete(from, to) });
    }
  }

  for (const [id, content] of winner) {
    const src = sources.get(id)!;
    if (!src.node.content.eq(content)) ops.push({ at: src.pos + 1, apply: (tr) => tr.replaceWith(src.pos + 1, src.pos + 1 + src.node.content.size, content) });
    for (const f of filed) {
      if (f.ref !== id || inDeleted(f.para.pos) || f.para.node.content.eq(content)) continue;
      ops.push({ at: f.para.pos + 1, apply: (tr) => tr.replaceWith(f.para.pos + 1, f.para.pos + 1 + f.para.node.content.size, content) });
    }
  }
  if (!ops.length) return null;

  // Back to front, so each op's positions are still those of `state`.
  const tr = state.tr;
  for (const op of ops.sort((a, b) => b.at - a.at)) op.apply(tr);
  return tr;
}

export const listMirrorKey = new PluginKey('listMirror');

export function listMirrorPlugin(): Plugin {
  return new Plugin({
    key: listMirrorKey,
    appendTransaction: (trs, old, state) => {
      if (!trs.some((t) => t.docChanged)) return null;
      const mapping = new Mapping();
      for (const t of trs) mapping.appendMapping(t.mapping);
      return syncMirrors(state, changes(trs), old, mapping);
    },
  });
}
